import SecureTokenStorage from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface Notification {
  id: string;
  type:
    | 'SYSTEM'
    | 'VERIFICATION'
    | 'PAYMENT'
    | 'COMMUNITY'
    | 'COMPLAINT'
    | 'APPEAL';
  title: string;
  message: string;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  expiresAt?: string | null;
  actionUrl?: string | null;
  actionText?: string | null;
  priority?: string;
  data?: Record<string, any>;
  userId: string;
  isArchived?: boolean;
  archivedAt?: string | null;
}

export interface NotificationFilters {
  type?: string;
  priority?: string;
  isRead?: boolean;
  isArchived?: boolean;
  limit?: number;
  offset?: number;
  search?: string;
}

export interface NotificationResponse {
  notifications: Notification[];
  total: number;
  unreadCount: number;
}

class NotificationService {
  private static getAuthHeaders() {
    const tokens = SecureTokenStorage.getTokens();
    return {
      'Content-Type': 'application/json',
      Authorization: tokens?.accessToken ? `Bearer ${tokens.accessToken}` : '',
    };
  }

  /**
   * Get user notifications with optional filters
   */
  static async getNotifications(
    filters: NotificationFilters = {}
  ): Promise<NotificationResponse> {
    const params = new URLSearchParams();

    if (filters.type) params.append('type', filters.type);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.isRead !== undefined)
      params.append('isRead', filters.isRead.toString());
    if (filters.isArchived !== undefined)
      params.append('isArchived', filters.isArchived.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());
    if (filters.search) params.append('search', filters.search);

    const response = await fetch(
      `${API_BASE_URL}/v1/notifications/user/me?${params.toString()}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch notifications');
    }

    return data.data;
  }

  /**
   * Get notification by ID
   */
  static async getNotificationById(
    notificationId: string
  ): Promise<Notification> {
    const response = await fetch(
      `${API_BASE_URL}/v1/notifications/${notificationId}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch notification');
    }

    return data.data.notification;
  }

  /**
   * Mark notification as read
   */
  static async markAsRead(notificationId: string): Promise<Notification> {
    const response = await fetch(
      `${API_BASE_URL}/v1/notifications/${notificationId}/read`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to mark notification as read');
    }

    return data.data.notification;
  }

  /**
   * Mark all notifications as read
   */
  static async markAllAsRead(): Promise<number> {
    const response = await fetch(`${API_BASE_URL}/v1/notifications/read-all`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || 'Failed to mark all notifications as read'
      );
    }

    return data.data.count || 0;
  }

  /**
   * Delete notification
   */
  static async deleteNotification(notificationId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/v1/notifications/${notificationId}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to delete notification');
    }
  }

  /**
   * Get unread notification count
   */
  static async getUnreadCount(): Promise<number> {
    try {
      const result = await this.getNotifications({ limit: 1 });
      return result.unreadCount;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Archive notification
   */
  static async archiveNotification(
    notificationId: string
  ): Promise<Notification> {
    const response = await fetch(
      `${API_BASE_URL}/v1/notifications/${notificationId}/archive`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to archive notification');
    }

    return data.data.notification;
  }
}

export default NotificationService;
